const FALLBACK_TIMEZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
]

export const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
        return 'UTC'
    }
}

export const persistUserTimezone = (timezone) => {
    if (!timezone || typeof window === 'undefined') return
    try {
        localStorage.setItem('userTimezone', timezone)
    } catch {
        // ignore storage errors
    }
}

export const getUserTimezone = () => {
    if (typeof window === 'undefined') {
        return 'UTC'
    }
    try {
        const dedicatedTimezone = localStorage.getItem('userTimezone')
        if (dedicatedTimezone) {
            return dedicatedTimezone
        }
        const userData = JSON.parse(localStorage.getItem('userData'))
        if (userData?.timezone) {
            return userData.timezone
        }
    } catch {
        // ignore parse errors
    }
    return getBrowserTimezone()
}

export const getServerStorageTimezone = () =>
    process.env.NEXT_PUBLIC_SERVER_TIMEZONE ||
    process.env.REACT_APP_SERVER_TIMEZONE ||
    'UTC'

const getTimezoneOffsetMs = (timeZone, date) => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'longOffset',
        }).formatToParts(date)
        const offsetName = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT'
        const match = offsetName.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/)
        if (!match) return 0
        const sign = match[1] === '+' ? 1 : -1
        const hours = Number(match[2])
        const minutes = Number(match[3] || 0)
        return sign * ((hours * 60) + minutes) * 60 * 1000
    } catch {
        return 0
    }
}

const parseServerDateTime = (value) => {
    if (!value) return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }

    const str = String(value).trim()
    if (!str) return null

    if (str.includes('T') && (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str))) {
        const date = new Date(str)
        return Number.isNaN(date.getTime()) ? null : date
    }

    const mysqlMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?(?:\.\d+)?$/)
    if (mysqlMatch) {
        const [, year, month, day, hour = '00', minute = '00', second = '00'] = mysqlMatch
        const storageTimezone = getServerStorageTimezone()

        if (storageTimezone === 'UTC') {
            const date = new Date(Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hour),
                Number(minute),
                Number(second),
            ))
            return Number.isNaN(date.getTime()) ? null : date
        }

        const naiveUtc = Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second),
        )
        const offsetMs = getTimezoneOffsetMs(storageTimezone, new Date(naiveUtc))
        const date = new Date(naiveUtc - offsetMs)
        return Number.isNaN(date.getTime()) ? null : date
    }

    const date = new Date(str)
    return Number.isNaN(date.getTime()) ? null : date
}

const getDatePartsInTimezone = (date, timeZone) => {
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    })

    const parts = formatter.formatToParts(date)
    const get = (type) => parts.find((part) => part.type === type)?.value || ''
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    }
}

export const formatMysqlDateTimeInUserTimezone = (value, options = {}) => {
    const date = parseServerDateTime(value)
    if (!date) return value || '—'

    const { timeZone = getUserTimezone() } = options
    const parts = getDatePartsInTimezone(date, timeZone)
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

const getTimezoneOffsetLabel = (timezone) => {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'shortOffset',
        })
        const parts = formatter.formatToParts(new Date())
        const offsetPart = parts.find((part) => part.type === 'timeZoneName')
        return offsetPart?.value || ''
    } catch {
        return ''
    }
}

export const getTimezoneOptions = () => {
    const timezones = typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : FALLBACK_TIMEZONES

    return timezones
        .map((timezone) => {
            const offset = getTimezoneOffsetLabel(timezone)
            return {
                value: timezone,
                label: offset ? `${timezone} (${offset})` : timezone,
            }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
}

export const formatDateTimeInUserTimezone = (value, options = {}) => {
    const date = parseServerDateTime(value)
    if (!date) return value || '—'

    const {
        timeZone = getUserTimezone(),
        year = 'numeric',
        month = 'short',
        day = 'numeric',
        hour = '2-digit',
        minute = '2-digit',
        second,
        hour12 = true,
        dateOnly = false,
        mysqlFormat = false,
    } = options

    if (mysqlFormat) {
        return formatMysqlDateTimeInUserTimezone(value, { timeZone })
    }

    const formatOptions = { timeZone, year, month, day }
    if (!dateOnly) {
        formatOptions.hour = hour
        formatOptions.minute = minute
        formatOptions.hour12 = hour12
        if (second !== undefined) {
            formatOptions.second = second
        }
    }

    return date.toLocaleString('en-US', formatOptions)
}

export const formatNowInUserTimezone = (options = {}) => {
    return formatDateTimeInUserTimezone(new Date(), options)
}

export const formatOrderDateTimeInUserTimezone = (value) => {
    return formatDateTimeInUserTimezone(value, {
        second: '2-digit',
    })
}
